import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, MessageSquare, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth, roleHomePath } from "@/contexts/AuthContext";
import { useOtpConfig, useSendLoginOtpMutation } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

const OtpLogin = () => {
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const { loginWithOtp } = useAuth();
  const { data: otpConfig } = useOtpConfig();
  const sendOtpMutation = useSendLoginOtpMutation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectPath =
    (location.state as { from?: string } | null)?.from ||
    searchParams.get("redirectTo") ||
    searchParams.get("returnUrl");

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast({
        title: "Invalid phone number",
        description: "Enter a valid 10-digit mobile number.",
        variant: "destructive",
      });
      return;
    }

    try {
      await sendOtpMutation.mutateAsync(phone.trim());
      setOtpSent(true);
      toast({
        title: "Code sent",
        description:
          otpConfig?.useDemoOtp && otpConfig.demoOtpCode
            ? `Demo mode — use code ${otpConfig.demoOtpCode}`
            : "Check your phone for the OTP.",
      });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const authUser = await loginWithOtp(phone.trim(), otp);
      if (!authUser) {
        toast({
          title: "Invalid OTP",
          description: "Check the code and try again.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Signed in with OTP" });
      const home = roleHomePath(authUser.role);
      const destination =
        redirectPath && redirectPath !== "/auth/login" && redirectPath !== "/auth/otp"
          ? redirectPath
          : home;
      navigate(destination, { replace: true });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        {!otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-5 p-6 sm:p-7">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                Mobile number
              </Label>
              <div className="relative">
                <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+91 98765 43210"
                  className="h-11 pl-9"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  disabled={sendOtpMutation.isPending}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                We&apos;ll send a one-time code via SMS.
                {otpConfig?.useDemoOtp ? " Demo mode is active." : ""}
              </p>
            </div>

            <Button
              type="submit"
              className="h-11 w-full text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-xs border-none"
              disabled={sendOtpMutation.isPending}
            >
              {sendOtpMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending OTP…
                </>
              ) : (
                "Send OTP"
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-5 p-6 sm:p-7">
            <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/50 p-3">
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  Code sent to {phone}
                </p>
                {otpConfig?.useDemoOtp && otpConfig.demoOtpCode ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Demo code:{" "}
                    <span className="font-mono font-semibold text-primary">
                      {otpConfig.demoOtpCode}
                    </span>
                  </p>
                ) : (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Valid for {otpConfig?.otpExpiryMinutes ?? 10} minutes.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Enter the 6-digit code</Label>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup className="gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="h-11 w-10 rounded-lg border-border text-sm"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                type="submit"
                className="h-11 w-full text-sm font-semibold"
                disabled={verifying || otp.length < 4}
              >
                {verifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Verify & sign in"
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="h-10 w-full text-sm text-muted-foreground"
                onClick={() => {
                  setOtpSent(false);
                  setOtp("");
                }}
              >
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                Change phone number
              </Button>
            </div>
          </form>
        )}
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Prefer email login?{" "}
        <Link to="/auth/login" className="font-semibold text-primary hover:underline">
          Sign in with password
        </Link>
      </p>
    </div>
  );
};

export default OtpLogin;
